import {
  createCameraApiClient,
  getOemCameraApiUrl,
  type CameraApiClient,
} from '@peritasai/data-access';
import type { PreviewStartOptions } from '@peritasai/types';
import { CameraSnapshotEntity } from './camera-snapshot.entity';

/**
 * Business-entity access to the stateless camera-service endpoints used by the OEM
 * tray assembly flow. Delegates HTTP to {@link CameraApiClient}.
 */
export class CameraRepository {
  constructor(private readonly apiClient: CameraApiClient) {}

  /** Start a preview; resolves with the MJPEG stream URL. */
  startPreview(options?: PreviewStartOptions): Promise<string> {
    return this.apiClient.startPreview(options);
  }

  /** Stop the active preview. */
  stopPreview(): Promise<void> {
    return this.apiClient.stopPreview();
  }

  /** Capture the current frame as a {@link CameraSnapshotEntity}. */
  async takeSnapshot(trayAssemblyId?: string): Promise<CameraSnapshotEntity> {
    const result = await this.apiClient.takeSnapshot(trayAssemblyId);
    return new CameraSnapshotEntity(result);
  }
}

export const cameraRepository = new CameraRepository(createCameraApiClient());

/**
 * Camera repository for the OEM tray flow. Resolves its base URL via
 * {@link getOemCameraApiUrl}, which falls back to the shared tray-assembly camera URL
 * until `VITE_OEM_CAMERA_API_URL` is set — so the OEM flow can be pointed at its own
 * camera-service instance (same host, different port) without affecting the
 * tray-assembly flow that uses {@link cameraRepository}.
 */
export const oemCameraRepository = new CameraRepository(
  createCameraApiClient(getOemCameraApiUrl)
);
